import React, { useState } from 'react';
import { Dropdown, Space, Typography, MenuProps, ConfigProvider } from '@app/ui';
import { DownOutlined, GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import {
    SUPPORTED_LANGUAGES,
    LANGUAGE_NAMES,
    SupportedLanguage,
} from '../../i18n/constants';

const ACTIVE_COLOR = '#0480B2';
const TEXT_COLOR = 'rgba(0, 0, 0, 0.88)';
const TEXT_COLOR_DESCRIPTION = 'rgba(0, 0, 0, 0.45)';
const DIVIDER_COLOR = 'rgba(0, 0, 0, 0.06)';

const LanguageSelector = () => {
    const { i18n, t } = useTranslation();
    const currentLanguage = (i18n.language || 'en-US') as SupportedLanguage;
    const [isOpen, setIsOpen] = useState(false);

    const handleLanguageChange: MenuProps['onClick'] = ({ key }) => {
        if (key !== 'header') {
            i18n.changeLanguage(key);
        }
    };

    const languageItems = SUPPORTED_LANGUAGES.map(langCode => ({
        key: langCode,
        label: LANGUAGE_NAMES[langCode],
    }));

    const items: MenuProps['items'] = [
        {
            key: 'header',
            label: (
                <span
                    style={{
                        color: TEXT_COLOR_DESCRIPTION,
                    }}
                >
                    {t('language.selectLanguage', 'Select Language')}
                </span>
            ),
            disabled: true,
            style: {
                cursor: 'default',
            },
        },
        ...languageItems.flatMap((item, index) =>
            index < languageItems.length - 1
                ? [item, { type: 'divider' as const }]
                : [item],
        ),
    ];

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorText: TEXT_COLOR,
                    colorTextDisabled: TEXT_COLOR_DESCRIPTION,
                    colorSplit: DIVIDER_COLOR,
                },
            }}
        >
            <Dropdown
                menu={{
                    items,
                    onClick: handleLanguageChange,
                    selectedKeys: [currentLanguage],
                }}
                trigger={['click']}
                placement="bottom"
                arrow
                align={{ offset: [0, 10] }}
                onOpenChange={setIsOpen}
            >
                <Space
                    style={{
                        cursor: 'pointer',
                    }}
                >
                    <GlobalOutlined
                        style={{
                            color: isOpen ? ACTIVE_COLOR : TEXT_COLOR,
                        }}
                    />
                    <Typography.Text
                        style={{
                            color: isOpen ? ACTIVE_COLOR : undefined,
                        }}
                    >
                        {LANGUAGE_NAMES[currentLanguage] || 'English'}
                    </Typography.Text>
                    <DownOutlined
                        style={{
                            fontSize: 12,
                            color: isOpen ? ACTIVE_COLOR : TEXT_COLOR,
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    />
                </Space>
            </Dropdown>
        </ConfigProvider>
    );
};

export default LanguageSelector;
